import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import useWorks from "../hooks/useWorks";
import useUsers from "../hooks/useUsers";
import AppContext from "../AppContext";
import _ from "lodash";
import useJournals from "../hooks/useJournals";
import { toast } from "react-toastify";

export const CART_STEP = {
  INFO: 1,
  METHOD: 2,
  PAYMENT: 3,
};

export const PAYMENT_METHOD = {
  CARD: 1,
  TRUEMONEY: 2,
  QR: 3,
};

export const CartContext = React.createContext({
  items: [],
  setItems: () => {},
  addItem: (id) => {},
  patchQty: (qty) => {},
  removeItem: async (id) => {},
  removeAllItems: async (id) => {},
  unavailableItems: [],
  setUnavailableItems: () => {},
  isDisabledPaymentBtn: true,
  setIsDisabledPaymentBtn: (item) => {},
  isFetchDetail: false,
  setIsFetchDetail: (item) => {},
  addSelections: (selections) => {},
  removeSelections: (selections) => {},
  clearSelections: () => {},
  selections: [],
  currentJournal: undefined,
  checkoutCart: async (includeVat) => {},
  step: CART_STEP.INFO,
  setStep: () => {},
  paymentMethod: undefined,
  setPaymentMethod: async (paymentMethod) => {},
  cardDetails: undefined,
  setCardDetails: (cardNo, name, expiration, cvv) => {},
  /**
   * Use when closing shopping cart
   */
  restartForm: async () => {},
  chargeJournal: async (type) => {},
  checkingOut: false,
  charging: false,
  charge: undefined,
  journal: undefined,
  chargeExpiresIn: undefined,
});

const CardProvider = ({ children }) => {
  const { user, isAuth } = useContext(AppContext);
  const [items, setItems] = useState(undefined);
  const [unavailableItems, setUnavailableItems] = useState([]);
  const [isDisabledButton, setDisabledButton] = useState(true);
  const [isFetchData, setIsFetchData] = useState(false);
  const [fetchDetail, setFetchDetail] = useState(false);
  const [isOpenShoppingCart, setOpenShoppingCart] = useState(false);
  const { queryItems, addItem, deleteItem, deleteItemsAll, patchQty } =
    useUsers();
  const { queryWorks } = useWorks();
  const [selections, setSelections] = useState([]);
  const {
    createJournal,
    chargeJournal,
    deleteChargeJournal,
    getChargeJournal,
  } = useJournals();
  const [journal, setJournal] = useState(undefined);
  const [step, setStep] = useState(CART_STEP.INFO);
  const [paymentMethod, setPaymentMethod] = useState(undefined);
  const [cardDetails, setCardDetails] = useState(undefined);
  const [charge, setCharge] = useState(undefined);
  const [checkingOut, setCheckingOut] = useState(false);
  const [charging, setCharging] = useState(false);
  const countdownIntervalRef = useRef(null);
  const fetchChargeIntervalRef = useRef(null);
  const [chargeExpiresIn, setChargeExpiresIn] = useState(undefined);

  useEffect(() => {
    // * Skip if not logged in
    if (!isAuth && !user) return;

    setIsFetchData(false);
    setOpenShoppingCart(false);
    fetchData();
    return () => {};
  }, [user, isAuth, isFetchData, isOpenShoppingCart]);

  useEffect(() => {
    if (step === CART_STEP.INFO) {
      setPaymentMethod(undefined);
    }

    // * Always scroll to top when step changed (Mobile support)
    const container = document.querySelector(".overlay");

    if (!container) return;

    container.scrollTop = 0;
  }, [step]);

  const _clearInvtervals = () => {
    setChargeExpiresIn(undefined);

    if (fetchChargeIntervalRef.current)
      clearInterval(fetchChargeIntervalRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
  };

  /**
   * Create or clear intervals for fetching charges for counting down time left
   */
  useEffect(() => {
    if (!charge) {
      _clearInvtervals();
      return;
    }

    // * Skip creating invterval if already exists
    if (charge && chargeExpiresIn) return;

    // * Skip if payment is card by checking expiresOn
    if (!charge.expiresOn) return;

    setChargeExpiresIn(
      (new Date(charge.expiresOn).getTime() - new Date().getTime()) / 100
    );

    // * Set interval to fetch charge status
    fetchChargeIntervalRef.current = setInterval(() => {
      getChargeJournal(journal._id).then(({ data: { status } }) => {
        if (charge.status === status) return;

        if (status === "successfull") _clearInvtervals();

        setCharge((curr) => ({
          ...curr,
          status,
        }));
      });
    }, 1000);

    // * Set interval to coutdown timer
    countdownIntervalRef.current = setInterval(() => {
      setChargeExpiresIn((time) => {
        if (time <= 0) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }

        return time - 1;
      });
    }, 1000);

    return () => {};
  }, [charge]);

  const fetchData = async () => {
    // * Query items
    const { data: _items } = await queryItems(
      user._id,
      {
        // active: true,
        // deleted: false,
      },
      null,
      null,
      {
        active: 1,
        deleted: 1,
      }
    );

    // * Query works
    if (_items.length === 0) {
      setItems([]);
      return;
    }

    const { data: _works } = await queryWorks(
      {
        _id: { $in: _items.map((item) => item.work) },
        // active: true,
        // deleted: false,
      },
      1,
      _items.length,
      {
        banner: 1,
        name: 1,
        price: 1,
        designType: 1,
        discount: 1,
        active: 1,
        deleted: 1,
      }
    );

    let data = [];
    let dataInactive = [];

    let workActive = _works.filter((w) => w.active && !w.deleted);

    let workInactive = _works.filter(
      (w) => w.deleted || (!w.active && !w.deleted)
    );

    workActive.map((o) => {
      let item = _items.find((i) => i.work === o._id);

      data.push(item);
    });

    workInactive.map((o) => {
      let item = _items.find((i) => i.work === o._id);

      dataInactive.push(item);
    });

    // * Set items with work detail
    setItems(
      data.map((item, index) => {
        const work = workActive.find((work) => work._id === item.work);

        if (!work) return item;

        return {
          ...item,
          isSelected: false,
          data: work,
        };
      })
    );

    setSelections(data.map((work) => work._id));

    setUnavailableItems(
      dataInactive.map((item, index) => {
        const work = workInactive.find((work) => work._id === item.work);

        if (!work) return item;

        return {
          ...item,
          isSelected: false,
          data: work,
        };
      })
    );
  };

  const handleAddItem = async (id, qty) => {
    const { data: _shoppingItem } = await addItem(user._id, {
      work: id,
      qty,
    });

    setIsFetchData(true);
  };

  const handleRemoveItem = async (itemId) => {
    await deleteItem(user._id, itemId);

    setItems((curr) => curr.filter((item) => item._id !== itemId));
    setUnavailableItems((curr) => curr.filter((item) => item._id !== itemId));
  };

  const handleRemoveAll = async (activeOnly) => {
    // * Delete all
    if (selections.length === 0 && !activeOnly) {
      await deleteItemsAll(user._id);
      setItems([]);
      setUnavailableItems([]);
      return;
    }

    if (selections.length === 0 && activeOnly) {
      for (const item of items) {
        await handleRemoveItem(item._id);
      }

      setItems([]);
      return;
    }

    // * Delete only selected
    for (const selection of selections) {
      await handleRemoveItem(selection);
    }
  };

  const handlePatchQty = (qty, itemId) => {
    patchQty(user._id, itemId, { qty });
    setIsFetchData(true);
  };

  const handleDisabledPaymentBtn = (item) => {
    setDisabledButton(item);
  };

  const handleFetchDetail = (item) => {
    setFetchDetail(item);
  };

  const handleOpenShoppingCart = (item) => {
    setOpenShoppingCart(item);
  };

  const handleAddSelections = (selections) => {
    setSelections((curr) => {
      const _current = _.cloneDeep(curr);

      for (const selection of selections) {
        if (_current.includes(selection)) {
          continue;
        }

        _current.push(selection);
      }

      return _current;
    });
  };

  const handleRemoveSelections = (selections) => {
    setSelections((curr) => {
      return curr.filter((c) => !selections.includes(c));
    });
  };

  const handleCheckoutCart = async (includeVat) => {
    setCheckingOut(true);

    try {
      const selected =
        selections.length === 0
          ? items
          : items.filter((i) => selections.includes(i._id));

      const details = selected.map((i) => ({
        work: i.work,
      }));

      // * Create journal
      const { data: journal } = await createJournal({
        details,
        includeVat: includeVat,
        company: user?.company,
      });

      setJournal(journal);

      if (journal.status !== 2) {
        setPaymentMethod(PAYMENT_METHOD.CARD);
        setStep(CART_STEP.PAYMENT);
        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCardDetails = (cardNo, name, expiration, cvv) => {
    setCardDetails({
      cardNo,
      name,
      expiration,
      cvv,
    });
  };

  const handleChargeJournal = async (paymentMethod) => {
    setCharging(true);

    if (paymentMethod === PAYMENT_METHOD.CARD) {
      return new Promise((resolve, reject) => {
        const { cardNo, name, expiration, cvv } = cardDetails;

        const [expirationMonth, expirationYear] = expiration.split("/");

        const tokenParameters = {
          expiration_month: expirationMonth,
          expiration_year: expirationYear,
          number: cardNo,
          security_code: cvv,
          name: name,
        };

        Omise.createToken(
          "card",
          tokenParameters,
          async (statusCode, response) => {
            if (!response || !response.card) {
              console.error("Error calling omise");
              toast.error(`${response.message}`);
              reject(response.message);
              return;
            }

            const cardToken = response.id;

            const { data: _charge } = await chargeJournal(journal._id, {
              method: "card",
              token: cardToken,
            });

            setCharge(_charge);
            setCharging(false);
            resolve(_charge);
          }
        );
      });
    }

    const { data: _charge } = await chargeJournal(journal._id, {
      method: paymentMethod === PAYMENT_METHOD.QR ? "promptpay" : "truemoney",
    });

    setCharge(_charge);
    setCharging(false);
    return _charge;
  };

  const expireCharge = async () => {
    // * Delete charge if used to have
    if (!journal) {
      return;
    }

    if (!paymentMethod || paymentMethod === PAYMENT_METHOD.CARD) return;

    if (charge && charge.status === "successfull") return;

    try {
      setCharge(undefined);
      await deleteChargeJournal(journal._id);
    } catch (error) {}
  };

  const handleRestartForm = async () => {
    setSelections([]);
    setStep(CART_STEP.INFO);
    setPaymentMethod(undefined);
    await expireCharge();
    setJournal(undefined);
    setCharge(undefined);
    setCardDetails(undefined);
    setCheckingOut(false);
    setCharging(false);
    setChargeExpiresIn(undefined);
  };

  const handleSetPaymentMethod = async (_paymentMethod) => {
    if (
      paymentMethod === PAYMENT_METHOD.QR ||
      paymentMethod === PAYMENT_METHOD.TRUEMONEY
    ) {
      await expireCharge();
    }

    setCharge(undefined);

    setPaymentMethod(_paymentMethod);

    // * Auto charge if not CARD
    if (_paymentMethod === PAYMENT_METHOD.CARD || !_paymentMethod) return;

    handleChargeJournal(_paymentMethod);
  };

  const contextValues = useMemo(
    () => ({
      items,
      setItems,
      unavailableItems,
      setUnavailableItems,
      addItem: handleAddItem,
      patchQty: handlePatchQty,
      removeItem: handleRemoveItem,
      removeAllItems: handleRemoveAll,
      isDisabledPaymentBtn: isDisabledButton,
      setIsDisabledPaymentBtn: handleDisabledPaymentBtn,
      isFetchDetail: fetchDetail,
      setIsFetchDetail: handleFetchDetail,
      isOpenShoppingcart: isOpenShoppingCart,
      setIsOpenShoppingCart: handleOpenShoppingCart,
      selections,
      addSelections: handleAddSelections,
      removeSelections: handleRemoveSelections,
      clearSelections: () => {
        setSelections([]);
      },
      currentJournal: journal,
      checkoutCart: handleCheckoutCart,
      step,
      setStep,
      paymentMethod,
      setPaymentMethod: handleSetPaymentMethod,
      restartForm: handleRestartForm,
      cardDetails,
      setCardDetails: handleCardDetails,
      chargeJournal: handleChargeJournal,
      charge,
      journal,
      checkingOut,
      charging,
      chargeExpiresIn,
    }),
    [
      items,
      unavailableItems,
      isDisabledButton,
      isFetchData,
      fetchDetail,
      isOpenShoppingCart,
      selections,
      step,
      paymentMethod,
      cardDetails,
      charge,
      journal,
      checkingOut,
      charging,
      chargeExpiresIn,
    ]
  );

  return (
    <CartContext.Provider value={contextValues}>
      {children}
    </CartContext.Provider>
  );
};

export default CardProvider;
